import { FlexBox, Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import type { BarChartInstrument, ChartInstrument } from 'lib-analytics-types';
import { resolveChartElementColor } from 'lib-analytics-ui';
import { memo } from 'react';
import { LegendProps as RechartsLegendProps } from 'recharts';

import styles from './chartInstrumentLegend.module.scss';

export type ChartInstrumentLegendProps = {
  /** The chart instrument. */
  instrument: ChartInstrument;
  /** The legend set data. */
  legend: string[];
  /** The visualization state of the chart instrument. */
  visualization: 'bar' | 'line';
  /** The handler to toggle legend items. */
  onToggleLegendItem: (label: string) => void;
  /** The mouse enter handler for legend items. */
  onMouseEnterLegendItem?: (label: string) => void;
  /** The mouse leave handler for legend items. */
  onMouseLeaveLegendItem?: (label: string) => void;
  /** Legend label that is being hovered over. */
  hoveredLegend?: string | null;
} & RechartsLegendProps;

export const ChartInstrumentLegend = memo(function ChartInstrumentLegend({
  instrument,
  legend,
  visualization,
  onToggleLegendItem,
  onMouseEnterLegendItem,
  onMouseLeaveLegendItem,
  hoveredLegend,
}: ChartInstrumentLegendProps): JSX.Element | null {
  const { series, options } = instrument.data as BarChartInstrument['data'];

  const { layout } = options?.legend ?? {
    layout: 'horizontal',
  };
  const isHorizontal = layout === 'horizontal';

  return (
    <FlexBox
      padding={isHorizontal ? '0' : '0 0 0 400'}
      flexDirection={isHorizontal ? 'row' : 'column'}
      gap={isHorizontal ? '400' : '200'}
      justifyContent="center"
    >
      {series.map(({ label, options }, index) => {
        const color = resolveChartElementColor(
          index,
          undefined,
          options?.color,
        );

        const legendItemClass = classnames(styles.legendItem, {
          [styles.legendItemInactive]: hoveredLegend && hoveredLegend !== label,
        });

        const legendIconClass = classnames(styles.legendIcon, {
          [styles.legendIconBar]: visualization === 'bar',
          [styles.legendIconLine]: visualization === 'line',
        });

        return (
          <FlexBox
            key={label}
            gap="200"
            flexDirection="row"
            alignItems="center"
            className={legendItemClass}
            onClick={() => onToggleLegendItem(label)}
            onMouseEnter={() => onMouseEnterLegendItem?.(label)}
            onMouseLeave={() => onMouseLeaveLegendItem?.(label)}
          >
            <span
              className={legendIconClass}
              style={{
                borderColor: color,
                backgroundColor: legend.includes(label) ? color : 'transparent',
              }}
            />
            <Text.Paragraph variant="4" subdued>
              {label}
            </Text.Paragraph>
          </FlexBox>
        );
      })}
    </FlexBox>
  );
});
